import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

/**
 * Thin wrapper around testResult.delete for use within the unified
 * assessment results delete hook. Invalidates both test-result and
 * assessment-level queries so that mixed-type pages refresh properly.
 */
export const useDeleteTestResultsForAssessmentsTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.testResult.delete.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey:
            trpc.frontend.testResult.latestTestResultsByControlId.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResults.queryKey(),
        });
        // COI-889: router-level key — the hand-listed set missed
        // `assessmentResultCount`, the Findings left-nav badge.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.assessment.pathKey(),
        });
        // COI-889: the left-nav badge reads this count family.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.testResult.testResultCount.queryKey(),
        });
        // COI-889: test results feed control effectiveness and the risk scores,
        // as the factory sibling `useDeleteTestResults` already declares.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.control.pathKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.risk.pathKey(),
        });
      },
    }),
  });

  return {
    deleteTestResults: async (ids: string[]): Promise<void> => {
      await mutation.mutateAsync({ ids });
    },
    isLoading: mutation.isPending,
  };
};
